import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const ACCOUNTS_KEY = 'accounts';

// ── Query: list accounts ──────────────────────────────────────────────────────
export function useAccounts(params = {}) {
  return useQuery({
    queryKey: [ACCOUNTS_KEY, params],
    queryFn: () => api.get('/accounts', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

// ── Query: single account (full detail) ──────────────────────────────────────
export function useAccount(id) {
  return useQuery({
    queryKey: [ACCOUNTS_KEY, id],
    queryFn: () => api.get(`/accounts/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Query: tech profile ───────────────────────────────────────────────────────
export function useTechProfile(accountId) {
  return useQuery({
    queryKey: [ACCOUNTS_KEY, accountId, 'tech-profile'],
    queryFn: () => api.get(`/accounts/${accountId}/tech-profile`).then((r) => r.data),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Query: sites ──────────────────────────────────────────────────────────────
export function useAccountSites(accountId) {
  return useQuery({
    queryKey: [ACCOUNTS_KEY, accountId, 'sites'],
    queryFn: () => api.get(`/accounts/${accountId}/sites`).then((r) => r.data),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Mutation: create account ──────────────────────────────────────────────────
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/accounts', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY] }),
  });
}

// ── Mutation: update account ──────────────────────────────────────────────────
export function useUpdateAccount(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/accounts/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, id] });
      qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY] });
    },
  });
}

// ── Mutation: delete account ──────────────────────────────────────────────────
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY] }),
  });
}

// ── Mutation: upsert tech profile ─────────────────────────────────────────────
export function useSaveTechProfile(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put(`/accounts/${accountId}/tech-profile`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId, 'tech-profile'] }),
  });
}

// ── Mutation: add site ────────────────────────────────────────────────────────
export function useAddSite(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/accounts/${accountId}/sites`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}

// ── Mutation: update site ─────────────────────────────────────────────────────
export function useUpdateSite(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, ...body }) =>
      api.patch(`/accounts/${accountId}/sites/${siteId}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}

// ── Mutation: delete site ─────────────────────────────────────────────────────
export function useDeleteSite(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId) => api.delete(`/accounts/${accountId}/sites/${siteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}

// ── Query: site tech profile ──────────────────────────────────────────────────
export function useSiteTechProfile(accountId, siteId) {
  return useQuery({
    queryKey: [ACCOUNTS_KEY, accountId, 'sites', siteId, 'tech-profile'],
    queryFn: () => api.get(`/accounts/${accountId}/sites/${siteId}/tech-profile`).then((r) => r.data),
    enabled: !!accountId && !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Mutation: upsert site tech profile ───────────────────────────────────────
export function useSaveSiteTechProfile(accountId, siteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.put(`/accounts/${accountId}/sites/${siteId}/tech-profile`, body).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId, 'sites', siteId, 'tech-profile'] }),
  });
}

// ── Mutation: link contact to account ────────────────────────────────────────
export function useLinkAccountContact(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/accounts/${accountId}/contacts`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}

// ── Mutation: update contact link (role, is_primary, site_id) ────────────────
export function useUpdateAccountContact(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, ...body }) => api.patch(`/accounts/${accountId}/contacts/${linkId}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}

// ── Mutation: unlink contact from account ────────────────────────────────────
export function useUnlinkAccountContact(accountId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.delete(`/accounts/${accountId}/contacts/${linkId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ACCOUNTS_KEY, accountId] }),
  });
}
